/** Key-value pair representing a custom property */
export interface CustomProperty {
    name: string;
    value: string;
}

/** Model containing custom properties grouped by parent entity ID */
export interface CustomPropertiesByParentModel {
    parentId: string;
    content: CustomProperty[];
}

/**
 * Interface for entities that can have custom properties attached.
 * Implemented by models that need help content or other custom metadata.
 */
export interface WithCustomProperties {
    customProperties: CustomProperty[];
}

type Entity = { sortOrder?: number };

/**
 * Merges custom properties into entities based on a matching ID field.
 *
 * @param entities - Array of entities to enhance with custom properties
 * @param variantTraitProperties - Array of custom properties grouped by parent ID
 * @param getEntityId - Function to extract the ID from an entity for matching
 * @returns New array of entities with customProperties field populated
 *
 * @example
 * const enrichedFacets = mergeCustomProperties(
 *     attributeFacets,
 *     customProps,
 *     (facet) => facet.attributeTypeId
 * );
 */
export function mergeCustomProperties<T extends Entity>(
    entities: T[],
    variantTraitProperties: CustomPropertiesByParentModel[],
    getEntityId: (entity: T) => string
): (T & WithCustomProperties)[] {    
    const sortedEntities = entities.slice().sort((a, b) => {
        if (a.sortOrder === undefined)
            return 1;

        if (b.sortOrder === undefined) 
            return -1;

        return (a).sortOrder - (b).sortOrder;        
    });

    if (!variantTraitProperties || variantTraitProperties.length === 0) {
        return sortedEntities.map(entity => ({
            ...entity,
            customProperties: [],
        }));
    }
    return sortedEntities.map(entity => {
        const entityId = getEntityId(entity);
        const customProps = variantTraitProperties.find(cp => cp.parentId === entityId);        
        return {
            ...entity,
            customProperties: customProps?.content ?? [],
        };
    });
}